/**
 * WalkCompanion Leaderboard Service
 * Leaderboard operations and analytics
 */

import { FirebaseService } from './FirebaseService';
import { Leaderboard, LeaderboardEntry, OrganizationRanking, ChallengeLevel, ApiResponse } from '../types';
import { calculateAverage, calculateSum } from '../utils/calculationUtils';
import { formatRank, formatPoints } from '../utils/formatUtils';

export class LeaderboardService {
  private firebaseService: FirebaseService;

  constructor(firebaseService: FirebaseService) {
    this.firebaseService = firebaseService;
  }

  // ============================================================================
  // LEADERBOARD OPERATIONS
  // ============================================================================

  /**
   * Load leaderboard for a challenge
   */
  async loadLeaderboard(challengeId: string, level: ChallengeLevel): Promise<Leaderboard | null> {
    try {
      return await this.firebaseService.loadLeaderboard(challengeId, level);
    } catch (error) {
      console.error('❌ Error loading leaderboard:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard entries for a challenge
   */
  async getLeaderboardEntries(
    challengeId: string,
    level: ChallengeLevel,
    _limit: number = 50
  ): Promise<LeaderboardEntry[]> {
    try {
      const leaderboard = await this.loadLeaderboard(challengeId, level);
      if (!leaderboard) {
        return [];
      }

      // This is a simplified implementation
      // In a real app, you'd have a separate leaderboard entries collection
      // or calculate this from participations
      console.log('🔍 Leaderboard entries calculation not fully implemented - would require additional data structure');
      return [];

    } catch (error) {
      console.error('❌ Error getting leaderboard entries:', error);
      return [];
    }
  }

  /**
   * Get user's rank in a leaderboard
   */
  async getUserRank(
    userId: string,
    challengeId: string,
    level: ChallengeLevel
  ): Promise<number | null> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      const userEntry = entries.find(entry => entry.userId === userId);
      return userEntry ? userEntry.rank : null;

    } catch (error) {
      console.error('❌ Error getting user rank:', error);
      return null;
    }
  }

  /**
   * Get top performers for a challenge
   */
  async getTopPerformers(
    challengeId: string,
    level: ChallengeLevel,
    limit: number = 10
  ): Promise<LeaderboardEntry[]> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level, limit);
      return entries.slice(0, limit);

    } catch (error) {
      console.error('❌ Error getting top performers:', error);
      return [];
    }
  }

  /**
   * Get organization rankings for global challenges
   */
  async getOrganizationRankings(challengeId: string): Promise<OrganizationRanking[]> {
    try {
      const leaderboard = await this.loadLeaderboard(challengeId, 'global');
      return leaderboard?.organizationRankings || [];

    } catch (error) {
      console.error('❌ Error getting organization rankings:', error);
      return [];
    }
  }

  // ============================================================================
  // LEADERBOARD ANALYTICS
  // ============================================================================

  /**
   * Get leaderboard statistics
   */
  async getLeaderboardStats(
    challengeId: string,
    level: ChallengeLevel
  ): Promise<{
    totalParticipants: number;
    averageProgress: number;
    totalProgress: number;
    topProgress: number;
    bottomProgress: number;
    medianProgress: number;
  } | null> {
    try {
      const leaderboard = await this.loadLeaderboard(challengeId, level);
      if (!leaderboard) {
        return null;
      }

      const entries = await this.getLeaderboardEntries(challengeId, level);
      if (entries.length === 0) {
        return {
          totalParticipants: 0,
          averageProgress: 0,
          totalProgress: 0,
          topProgress: 0,
          bottomProgress: 0,
          medianProgress: 0
        };
      }

      const progressValues = entries.map(entry => entry.progress);
      const sortedProgress = progressValues.sort((a, b) => a - b);

      return {
        totalParticipants: entries.length,
        averageProgress: calculateAverage(progressValues),
        totalProgress: calculateSum(progressValues),
        topProgress: Math.max(...progressValues),
        bottomProgress: Math.min(...progressValues),
        medianProgress: sortedProgress[Math.floor(sortedProgress.length / 2)] || 0
      };

    } catch (error) {
      console.error('❌ Error getting leaderboard stats:', error);
      return null;
    }
  }

  /**
   * Get user's performance summary
   */
  async getUserPerformanceSummary(
    userId: string,
    challengeId: string,
    level: ChallengeLevel
  ): Promise<{
    rank: number | null;
    totalParticipants: number;
    percentile: number;
    progress: number;
    pointsEarned: number;
    isInTop10: boolean;
    isInTop25: boolean;
    isInTop50: boolean;
  } | null> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      const userEntry = entries.find(entry => entry.userId === userId);

      if (!userEntry) {
        return null;
      }

      const totalParticipants = entries.length;
      const percentile = totalParticipants > 0 ? ((totalParticipants - userEntry.rank + 1) / totalParticipants) * 100 : 0;

      return {
        rank: userEntry.rank,
        totalParticipants,
        percentile,
        progress: userEntry.progress,
        pointsEarned: userEntry.pointsEarned,
        isInTop10: userEntry.rank <= 10,
        isInTop25: userEntry.rank <= 25,
        isInTop50: userEntry.rank <= 50
      };

    } catch (error) {
      console.error('❌ Error getting user performance summary:', error);
      return null;
    }
  }

  /**
   * Get challenge completion rate
   */
  async getChallengeCompletionRate(
    challengeId: string,
    level: ChallengeLevel
  ): Promise<number> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      if (entries.length === 0) {
        return 0;
      }

      const completedEntries = entries.filter(entry => entry.progress >= 1);
      return (completedEntries.length / entries.length) * 100;

    } catch (error) {
      console.error('❌ Error getting challenge completion rate:', error);
      return 0;
    }
  }

  // ============================================================================
  // LEADERBOARD COMPARISONS
  // ============================================================================

  /**
   * Compare user performance with others
   */
  async compareUserPerformance(
    userId: string,
    challengeId: string,
    level: ChallengeLevel
  ): Promise<{
    userRank: number | null;
    totalParticipants: number;
    betterThanPercentage: number;
    averageProgress: number;
    userProgress: number;
    progressDifference: number;
    rankDifference: number | null;
  } | null> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      const userEntry = entries.find(entry => entry.userId === userId);

      if (!userEntry) {
        return null;
      }

      const totalParticipants = entries.length;
      const betterThanPercentage = totalParticipants > 0 ? ((totalParticipants - userEntry.rank + 1) / totalParticipants) * 100 : 0;
      const averageProgress = calculateAverage(entries.map(entry => entry.progress));
      const progressDifference = userEntry.progress - averageProgress;

      return {
        userRank: userEntry.rank,
        totalParticipants,
        betterThanPercentage,
        averageProgress,
        userProgress: userEntry.progress,
        progressDifference,
        rankDifference: null // Would need historical data to calculate
      };

    } catch (error) {
      console.error('❌ Error comparing user performance:', error);
      return null;
    }
  }

  /**
   * Get nearby competitors
   */
  async getNearbyCompetitors(
    userId: string,
    challengeId: string,
    level: ChallengeLevel,
    range: number = 5
  ): Promise<LeaderboardEntry[]> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      const userEntry = entries.find(entry => entry.userId === userId);

      if (!userEntry) {
        return [];
      }

      const startIndex = Math.max(0, userEntry.rank - range - 1);
      const endIndex = Math.min(entries.length, userEntry.rank + range);

      return entries.slice(startIndex, endIndex).filter(entry => entry.userId !== userId);

    } catch (error) {
      console.error('❌ Error getting nearby competitors:', error);
      return [];
    }
  }

  // ============================================================================
  // LEADERBOARD FILTERING
  // ============================================================================

  /**
   * Get leaderboard entries by organization
   */
  async getLeaderboardByOrganization(
    challengeId: string,
    organizationId: string,
    level: ChallengeLevel
  ): Promise<LeaderboardEntry[]> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      return entries.filter(entry => entry.organizationId === organizationId);

    } catch (error) {
      console.error('❌ Error getting leaderboard by organization:', error);
      return [];
    }
  }

  /**
   * Get leaderboard entries by team
   */
  async getLeaderboardByTeam(
    challengeId: string,
    teamId: string,
    level: ChallengeLevel
  ): Promise<LeaderboardEntry[]> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      return entries.filter(entry => entry.teamId === teamId);

    } catch (error) {
      console.error('❌ Error getting leaderboard by team:', error);
      return [];
    }
  }

  /**
   * Get leaderboard entries by progress range
   */
  async getLeaderboardByProgressRange(
    challengeId: string,
    level: ChallengeLevel,
    minProgress: number,
    maxProgress: number
  ): Promise<LeaderboardEntry[]> {
    try {
      const entries = await this.getLeaderboardEntries(challengeId, level);
      return entries.filter(entry => entry.progress >= minProgress && entry.progress <= maxProgress);

    } catch (error) {
      console.error('❌ Error getting leaderboard by progress range:', error);
      return [];
    }
  }

  // ============================================================================
  // LEADERBOARD UPDATES
  // ============================================================================

  /**
   * Update leaderboard for a challenge
   * This would typically be called by a background process
   */
  async updateLeaderboard(
    _challengeId: string,
    _level: ChallengeLevel
  ): Promise<ApiResponse<void>> {
    try {
      // This is a placeholder for leaderboard update logic
      // In a real implementation, this would:
      // 1. Get all participations for the challenge
      // 2. Calculate rankings
      // 3. Update the leaderboard in Firebase
      
      console.log('🔄 Leaderboard update not implemented - would require background processing');
      return { success: true };

    } catch (error) {
      console.error('❌ Error updating leaderboard:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update leaderboard'
      };
    }
  }

  /**
   * Recalculate all leaderboards
   * This would typically be called by an admin function
   */
  async recalculateAllLeaderboards(): Promise<ApiResponse<void>> {
    try {
      // This is a placeholder for bulk leaderboard recalculation
      console.log('🔄 Bulk leaderboard recalculation not implemented - would require admin interface');
      return { success: true };

    } catch (error) {
      console.error('❌ Error recalculating leaderboards:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to recalculate leaderboards'
      };
    }
  }

  // ============================================================================
  // LEADERBOARD NOTIFICATIONS
  // ============================================================================

  /**
   * Check for leaderboard achievements
   */
  async checkLeaderboardAchievements(
    userId: string,
    challengeId: string,
    level: ChallengeLevel
  ): Promise<{
    newRank: number | null;
    rankChange: number | null;
    isTop10: boolean;
    isTop25: boolean;
    isTop50: boolean;
    achievements: string[];
  }> {
    try {
      const performance = await this.getUserPerformanceSummary(userId, challengeId, level);
      
      if (!performance) {
        return {
          newRank: null,
          rankChange: null,
          isTop10: false,
          isTop25: false,
          isTop50: false,
          achievements: []
        };
      }

      const achievements: string[] = [];
      
      if (performance.isInTop10) {
        achievements.push('Top 10 Performer');
      }
      if (performance.isInTop25) {
        achievements.push('Top 25 Performer');
      }
      if (performance.isInTop50) {
        achievements.push('Top 50 Performer');
      }
      if (performance.progress >= 1) {
        achievements.push('Challenge Completed');
      }

      return {
        newRank: performance.rank,
        rankChange: null, // Would need historical data
        isTop10: performance.isInTop10,
        isTop25: performance.isInTop25,
        isTop50: performance.isInTop50,
        achievements
      };

    } catch (error) {
      console.error('❌ Error checking leaderboard achievements:', error);
      return {
        newRank: null,
        rankChange: null,
        isTop10: false,
        isTop25: false,
        isTop50: false,
        achievements: []
      };
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get Firebase service instance
   */
  getFirebaseService(): FirebaseService {
    return this.firebaseService;
  }

  /**
   * Format leaderboard entry for display
   */
  formatLeaderboardEntry(entry: LeaderboardEntry): {
    rank: string;
    displayName: string;
    progress: string;
    points: string;
    organization: string;
  } {
    return {
      rank: formatRank(entry.rank),
      displayName: entry.displayName,
      progress: `${(entry.progress * 100).toFixed(1)}%`,
      points: formatPoints(entry.pointsEarned),
      organization: entry.organizationName || 'Individual'
    };
  }

  /**
   * Validate leaderboard data
   */
  validateLeaderboardData(leaderboard: Partial<Leaderboard>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (leaderboard.totalParticipants !== undefined && leaderboard.totalParticipants < 0) {
      errors.push('Total participants cannot be negative');
    }

    if (leaderboard.averageProgress !== undefined && (leaderboard.averageProgress < 0 || leaderboard.averageProgress > 1)) {
      errors.push('Average progress must be between 0 and 1');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
} 